import { ADAPTER_OPTIONS, adapterSelectValue } from '@/lib/api/adapterTypes';
import { useMemo } from 'react';

/**
 * Adapter Type picker (mockup `fsl-rtm-map-mu` → Custodian Configuration).
 *
 * `value` is the dropdown's current selection (see `adapterSelectValue`).
 * A stored type we don't list — a backend-added adapter — is appended as an
 * extra selected option, so a value the aircraft really has never reads as
 * "nothing selected" (a controlled <select> whose value matches no <option>
 * shows as blank). The "— Select —" row mirrors the mockup and stands for
 * "no adapter", which can't be saved: the endpoint has no way to clear it.
 */
export function AdapterTypeSelect({
  value,
  storedType,
  onChange,
  disabled = false,
}: {
  value: string;
  /** The aircraft's stored `adapter_type`, used to keep an unlisted value visible. */
  storedType: string | null | undefined;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const extra = useMemo(() => {
    const stored = adapterSelectValue(storedType);
    return stored && !ADAPTER_OPTIONS.some((o) => o.value === stored) ? stored : null;
  }, [storedType]);

  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-2.5 py-[7px]">
      <select
        data-testid="adapter-type-select"
        aria-label="Adapter Type"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-[0.923em] font-bold leading-none outline-none font-mono disabled:opacity-60"
      >
        <option value="">— Select —</option>
        {ADAPTER_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {extra && <option value={extra}>{extra}</option>}
      </select>
      <div className="text-[0.615em] uppercase tracking-wide font-mono text-(--color-text3) mt-1">
        Adapter Type
      </div>
    </div>
  );
}
